import { IconButton } from '@creditornot/cb-button';
import { InputContainer, InputContainerProps } from '@creditornot/cb-form';
import { ChainLink, DocumentEditSimple } from '@creditornot/cb-icons';
import { LegacyPopover } from '@creditornot/cb-popover';
import { Spinner } from '@creditornot/cb-progress';
import { useMutation } from '@tanstack/react-query';
import React, { ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useField, useForm, useFormState } from 'react-final-form';
import toast from 'react-hot-toast';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { getProductFromSource, getProductInformationSettings } from 'apis/restaurant-api/merchant-admin/menu2/api';
import ErrorToastContent from 'common/components/ErrorToastContent';
import { Typography as T } from 'common/typography';
import { MenuLocaleContext } from 'modules/menu/MenuLocaleContext';
import { MenuMetaContext } from 'modules/menu/MenuMetaContext';

import { FeedFormValues, FeedOverrideFieldNames, FormValues } from './types';
import { transformFeedProductToFormValues } from '../../transformers';

type RenderProps = {
  disabled?: boolean;
};

type Props = Omit<InputContainerProps, 'children'> & {
  name: FeedOverrideFieldNames;
  isSynced: boolean;
  children: ReactNode | (({ disabled }: RenderProps) => ReactNode);
  onDisable?: (disabled: boolean) => void;
};

const FieldContainer = styled.div`
  display: flex;
  align-items: center;
`;

const ContentContainer = styled.div`
  /* Ellipsis overflow text */
  min-width: 0;
  flex: 1;
`;

const StyledPopover = styled(LegacyPopover)`
  display: flex;
  align-items: center;
`;

const StyledIconButton = styled(IconButton)`
  margin-inline-start: 8px;
`;

const SpinnerContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 24px;
  margin-inline-start: 8px;
`;

export const useFeedOverrides = (feedOverrideFieldName: FeedOverrideFieldNames) => {
  const [userFormValues, setUserFormValues] = useState<Partial<FormValues>>({});

  const form = useForm<FormValues>();
  const { input: sourceConfigInput } = useField<'USER_INPUT' | undefined>(`sourceConfig.${feedOverrideFieldName}`);
  const { values } = useFormState<FormValues>();

  const isUserInput = useMemo(() => sourceConfigInput.value === 'USER_INPUT', [sourceConfigInput.value]);

  const relinkFormFields = useCallback(
    (transformedData: Partial<FeedFormValues>) => {
      setUserFormValues(
        feedOverrideFieldName === 'nutritionFields'
          ? {
              nutritionFacts: values.nutritionFacts,
              nutritionValues: values.nutritionValues,
              nutritionValuesBaseline: values.nutritionValuesBaseline,
            }
          : { [feedOverrideFieldName]: values[feedOverrideFieldName] },
      );

      sourceConfigInput.onChange(undefined);
      if (feedOverrideFieldName === 'image') {
        form.change(feedOverrideFieldName, transformedData.image ?? []);
      } else if (feedOverrideFieldName === 'nutritionFields') {
        form.change('nutritionFacts', transformedData.nutritionFacts);
        form.change('nutritionValues', transformedData.nutritionValues);
        form.change('nutritionValuesBaseline', transformedData.nutritionValuesBaseline);
      } else {
        form.change(feedOverrideFieldName, transformedData[feedOverrideFieldName]);
      }
    },
    [feedOverrideFieldName, form, sourceConfigInput, values],
  );

  const overrideFormFields = useCallback(() => {
    sourceConfigInput.onChange('USER_INPUT');
    form.batch(() => {
      if (Object.values(userFormValues).some(value => value === 0 || !!value)) {
        Object.entries(userFormValues).forEach(([key, value]) => {
          form.change(key as keyof FormValues, value);
        });
      }
    });
  }, [form, sourceConfigInput, userFormValues]);

  return { relinkFormFields, overrideFormFields, isUserInput };
};

const FeedInputContainer = ({ name, isSynced, children, onDisable, ...inputContainerProps }: Props) => {
  const intl = useIntl();

  const { menuId } = useContext(MenuMetaContext);
  const { primaryLanguage: language } = useContext(MenuLocaleContext);

  const { relinkFormFields, overrideFormFields, isUserInput } = useFeedOverrides(name);

  const {
    input: { value: sourceCountry },
  } = useField<string | undefined>('sourceCountry');
  const {
    input: { value: gtin },
  } = useField<string>('barcodeGtin');

  const { mutate: handleRelinkToFeed, isLoading } = useMutation(
    async () => {
      if (!sourceCountry) {
        throw new Error('No source country identified, this should not happen');
      }
      const product = await getProductFromSource(menuId, gtin, sourceCountry);
      const { nutrition_values: nutritionSettings } = await getProductInformationSettings(menuId);
      const transformedData = transformFeedProductToFormValues(product, { language, nutritionSettings });
      relinkFormFields(transformedData);
    },
    {
      onError: error => {
        toast.error(<ErrorToastContent error={error} />, { duration: Infinity });
      },
    },
  );

  const disabled = useMemo(
    () => isSynced && ((!!sourceCountry && !isUserInput) || isLoading),
    [isLoading, isSynced, isUserInput, sourceCountry],
  );

  useEffect(() => {
    onDisable?.(disabled);
  }, [disabled, onDisable]);

  return (
    <InputContainer {...inputContainerProps}>
      <FieldContainer>
        <ContentContainer>{typeof children === 'function' ? children({ disabled }) : children}</ContentContainer>
        {isSynced && !!sourceCountry && (
          <StyledPopover
            placement="top"
            renderArrow={false}
            showOnClick={false}
            disabled={isLoading}
            content={
              <T.Body3>
                {intl.formatMessage({
                  id: isUserInput ? 'menu-item.feed-field-relink' : 'menu-item.feed-field-override',
                })}
              </T.Body3>
            }
          >
            {isLoading ? (
              <SpinnerContainer>
                <Spinner size="small" />
              </SpinnerContainer>
            ) : (
              <StyledIconButton
                size="tiny"
                variant="brand"
                background="transparent"
                onClick={isUserInput ? () => handleRelinkToFeed() : () => overrideFormFields()}
              >
                {isUserInput ? <ChainLink /> : <DocumentEditSimple />}
              </StyledIconButton>
            )}
          </StyledPopover>
        )}
      </FieldContainer>
    </InputContainer>
  );
};

export default FeedInputContainer;
